import { lazy, Suspense, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { ArrowRightIcon as ArrowRight } from "@/components/icons/HeaderIcons";
import type { ShopifyProduct } from "@/lib/shopify";

import LazySection from "@/components/LazySection";
import AnimatedSection from "@/components/AnimatedSection";
import USPBar from "@/components/USPBar";
import { useHomepageData } from "@/hooks/useHomepageData";
import { lazyWithRetry } from "@/lib/lazyWithRetry";

const CollectionBanners = lazyWithRetry(() => import("@/components/CollectionBanners"));
const ShopifyProductCard = lazyWithRetry(() => import("@/components/ShopifyProductCard"));
const PopularBrands = lazyWithRetry(() => import("@/components/PopularBrands"));
const HomepageSEOContent = lazyWithRetry(() => import("@/components/HomepageSEOContent"));
const NewsletterSignupSimple = lazyWithRetry(() => import("@/components/NewsletterSignupSimple"));
const CampaignBanners = lazyWithRetry(() => import("@/components/CampaignBanners"));
const TipsSection = lazyWithRetry(() => import("@/components/TipsSection"));
const AnimalProductsBlock = lazyWithRetry(() => import("@/components/AnimalProductsBlock"));
const NewArrivalsBlock = lazyWithRetry(() => import("@/components/NewArrivalsBlock"));
const BrandSpotlightBanners = lazyWithRetry(() => import("@/components/BrandSpotlightBanners"));
const WhyZooi = lazyWithRetry(() => import("@/components/WhyZooi"));

/* ─── HERO ─── */
const hideShell = () => {
  const shell = document.getElementById('shell');
  if (shell && !shell.classList.contains('shell-hidden')) {
    shell.classList.add('shell-hidden');
  }
};

const HeroCampaign = () => {
  // If the static shell-hero from index.html is still in the DOM (initial home
  // load), we MUST NOT render a duplicate <img> here — that would create a
  // second LCP candidate and push lab LCP from ~2s to >10s on slow 4G.
  // The shell hero stays as the visual LCP element; we render only the
  // interactive text/CTA overlay on top of it.
  // On client-side navigation back to "/", the shell is gone and we render
  // the full <picture> normally so the hero still appears.
  const [hasShellHero] = useState<boolean>(() => {
    if (typeof document === "undefined") return false;
    const s = document.getElementById("shell");
    return !!s && !s.classList.contains("shell-hidden");
  });

  const handleImageReady = () => hideShell();

  // Failsafe: när hasShellHero=true renderas ingen React-<img>, så onLoad i
  // handleImageReady fyrar aldrig och shellen blir kvar som absolut overlay
  // (z-index:50) ovanpå Header/USP-raden och .shell-nav syns som ett mörkt
  // band under hero. LCP är redan målt på shell-bilden vid det här laget —
  // det är säkert att gömma shellen så fort React-overlayen har committat.
  useEffect(() => {
    if (!hasShellHero) return;
    const raf = requestAnimationFrame(() => hideShell());
    return () => cancelAnimationFrame(raf);
  }, [hasShellHero]);


  return (
    <section className="relative w-full overflow-hidden">
      <div className="relative h-[400px] md:h-[500px]">
        <div className="absolute inset-0">
          {!hasShellHero && (
            <picture>
              <source type="image/avif" srcSet="/hero-mobile-400w.avif" media="(max-width: 767px)" />
              <source type="image/webp" srcSet="/hero-mobile-400w.webp" media="(max-width: 767px)" />
              <source type="image/avif" srcSet="/hero-lifestyle-800w.avif 800w, /hero-lifestyle-1200w.avif 1200w" media="(min-width: 768px)" sizes="100vw" />
              <source type="image/webp" srcSet="/hero-lifestyle-800w.webp 800w, /hero-lifestyle-1200w.webp 1200w" media="(min-width: 768px)" sizes="100vw" />
              <img
                src="/hero-lifestyle-1200w.webp"
                alt="Hund och katt – foder och tillbehör hos Zooi"
                className="w-full h-full object-cover"
                loading="eager"
                decoding="sync"
                width={1200}
                height={675}
                onLoad={handleImageReady}
                // @ts-expect-error React 18 lowercases fetchpriority
                fetchpriority="high"
              />
            </picture>
          )}
          {/* Gradient overlay is rendered in both cases — it sits on top of
              either the shell hero (initial) or the React hero (client nav). */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/20" />
        </div>
        <div className="relative container h-full flex items-center px-4 md:px-6">
          <div className="max-w-xl space-y-4">
            <div className="hidden md:inline-flex items-center gap-2 bg-primary/90 text-primary-foreground px-4 py-2.5 rounded-full text-xs md:text-sm font-bold uppercase tracking-wide shadow-md">
              <span>🎉 Öppningskampanj – upp till 50% rabatt</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-[3.25rem] font-bold text-white leading-[1.1] tracking-tight">
              Zooi — din djuraffär online för hund, katt och smådjur
              <br />
              <span className="text-primary-300">Ny sajt, nya priser – fira med oss!</span>
            </h1>
            <p className="text-base md:text-lg text-white/95 max-w-md leading-relaxed">
              Vi har öppnat vår nya butik och firar med superpriser på foder, leksaker och tillbehör för hund, katt och smådjur. Passa på innan kampanjen tar slut!
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary-700 text-primary-foreground font-bold px-8 h-12 rounded-full shadow-lg hover:shadow-xl hover:scale-[1.03] transition-all"
                asChild
              >
                <Link to="/kampanj/hundfoder">
                  50% på foder
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
              <Button
                size="lg"
                className="bg-white text-foreground hover:bg-white/90 font-bold px-8 h-12 rounded-full shadow-lg hover:shadow-xl hover:scale-[1.03] transition-all"
                asChild
              >
                <Link to="/produkter">
                  Se alla erbjudanden
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1">
              <Link to="/hund" className="text-sm text-white hover:text-white underline-offset-2 hover:underline transition-colors font-semibold">Hund</Link>
              <span className="text-white/70">·</span>
              <Link to="/katt" className="text-sm text-white hover:text-white underline-offset-2 hover:underline transition-colors font-semibold">Katt</Link>
              <span className="text-white/70">·</span>
              <Link to="/smådjur" className="text-sm text-white hover:text-white underline-offset-2 hover:underline transition-colors font-semibold">Smådjur</Link>
              <span className="text-white/70">·</span>
              <Link to="/outlet" className="text-sm text-white hover:text-white underline-offset-2 hover:underline transition-colors font-semibold">Outlet</Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── PRODUCT GRID ─── */
const ProductGrid = ({
  items,
  count = 4,
  loading,
}: {
  items: ShopifyProduct[];
  count?: number;
  loading: boolean;
}) =>
  loading ? (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
      {[...Array(count)].map((_, i) => (
        <div key={i} className="aspect-[3/4] bg-muted/40 rounded-xl animate-pulse" />
      ))}
    </div>
  ) : (
    <Suspense fallback={
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
        {[...Array(count)].map((_, i) => (
          <div key={i} className="aspect-[3/4] bg-muted/40 rounded-xl animate-pulse" />
        ))}
      </div>
    }>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
        {items.map((product) => (
          <ShopifyProductCard key={product.node.id} product={product} />
        ))}
      </div>
    </Suspense>
  );

/* ─── MAIN PAGE ─── */
const CampaignHomePage = () => {
  const { data, isLoading } = useHomepageData();

  return (
    <>
      <Helmet>
        <title>Zooi – Djuraffär online för hund, katt, smådjur & akvarium</title>
        <meta
          name="description"
          content="Handla foder, leksaker & tillbehör till hund, katt och smådjur. Fri frakt över 799 kr, 14 dagars öppet köp och snabb leverans hos Zooi.se."
        />
        <link rel="canonical" href="https://zooi.se/" />
        <meta name="theme-color" content="#4a6741" />
        {/* No language-alternate link — site is single-language sv-SE.
            A solo language link without reciprocal entries is invalid per Google
            and triggers the "missing back link" warning in Merchant Center. */}
        <meta property="og:title" content="Zooi – Djuraffär online för hund, katt, smådjur & akvarium" />
        <meta
          property="og:description"
          content="Foder, leksaker och tillbehör för hund, katt, smådjur, fågel och akvarium. Snabb leverans och fri frakt över 799 kr (upp till 15 kg)."
        />
        <meta property="og:url" content="https://zooi.se/" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Zooi",
          "legalName": "Party & Event i Norr AB",
          "url": "https://zooi.se",
          "logo": "https://zooi.se/zooi-logo.png",
          "description": "Handla foder, leksaker, tillbehör och omvårdnad för hund, katt, smådjur och akvarium. Snabb leverans och fri frakt över 799 kr (upp till 15 kg).",
          "email": "info@zooi.se",
          "telephone": "+46920550620",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Bojvägen 6",
            "addressLocality": "Rosvik",
            "postalCode": "945 34",
            "addressCountry": "SE"
          },
          "vatID": "SE559468206301",
          "sameAs": [],
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+46920550620",
            "email": "info@zooi.se",
            "contactType": "customer service",
            "availableLanguage": "Swedish"
          }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Zooi",
          "url": "https://zooi.se",
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": "https://zooi.se/sok?q={search_term_string}"
            },
            "query-input": "required name=search_term_string"
          }
        })}</script>
      </Helmet>

      {/* ── 1. HERO ── */}
      <HeroCampaign />

      {/* ── 2. TRUST BAR ── */}
      <USPBar />

      {/* ── 3. DJURINGÅNGAR ── */}
      <Suspense fallback={<div className="py-10 md:py-14 bg-muted/30"><div className="container px-4 md:px-6"><div className="grid grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">{[...Array(6)].map((_,i)=><div key={i} className="aspect-square rounded-2xl bg-muted/50 animate-pulse"/>)}</div></div></div>}>
        <CollectionBanners />
      </Suspense>

      {/* ── 4. KAMPANJERBJUDANDEN ── */}
      <LazySection minHeight="420px" rootMargin="600px">
        <CampaignBanners />
      </LazySection>

      {/* ── 5. VARUMÄRKES-SPOTLIGHT ── */}
      <LazySection minHeight="600px" rootMargin="600px">
        <BrandSpotlightBanners />
      </LazySection>

      {/* ── 6. POPULÄRT JUST NU ── */}
      <section className="py-10 md:py-14 bg-muted/30">
        <div className="container px-4 md:px-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground">
                Populärt just nu
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Produkter som många kunder väljer just nu
              </p>
            </div>
            <Link
              to="/produkter"
              className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1"
            >
              Se alla
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <ProductGrid items={data?.popular || []} count={8} loading={isLoading} />
        </div>
      </section>

      {/* ── 7. NYHETER I BUTIKEN ── */}
      <LazySection minHeight="520px" rootMargin="600px">
        <AnimatedSection>
          <NewArrivalsBlock count={4} products={data?.newArrivals} />
        </AnimatedSection>
      </LazySection>

      {/* ── 8. UTVALT FÖR HUND ── */}
      <LazySection minHeight="520px" rootMargin="400px">
        <AnimatedSection>
          <AnimalProductsBlock
            title="Utvalt för hund"
            subtitle="Vardagsfavoriter för promenad, lek, vila och matstund"
            animalTag="hund"
            viewAllHref="/hund"
            viewAllLabel="Se allt för hund"
            bgClass="bg-muted/30"
            excludeL2={["L2:vard-halsa"]}
            products={data?.animals?.hund}
          />
        </AnimatedSection>
      </LazySection>

      {/* ── 9. UTVALT FÖR KATT ── */}
      <LazySection minHeight="520px" rootMargin="400px">
        <AnimatedSection>
          <AnimalProductsBlock
            title="Utvalt för katt"
            subtitle="Mat, sand, klös och lek – utvalt för ett enklare kattliv"
            animalTag="katt"
            viewAllHref="/katt"
            viewAllLabel="Se allt för katt"
            excludeL2={["L2:vard-halsa"]}
            products={data?.animals?.katt}
          />
        </AnimatedSection>
      </LazySection>

      {/* ── 10–13. Remaining animals – keep LazySection for deep sections ── */}
      <LazySection minHeight="520px" rootMargin="400px">
        <AnimatedSection>
          <AnimalProductsBlock
            title="Utvalt för smådjur"
            subtitle="Bur, foder och tillbehör för kanin, hamster och gnagare"
            animalTag="smadjur"
            viewAllHref="/smådjur"
            viewAllLabel="Se allt för smådjur"
            bgClass="bg-muted/30"
            products={data?.animals?.smadjur}
          />
        </AnimatedSection>
      </LazySection>

      <LazySection minHeight="520px" rootMargin="400px">
        <AnimatedSection>
          <AnimalProductsBlock
            title="Utvalt för fågel"
            subtitle="Foder, burar och leksaker för din fågel"
            animalTag="fagel"
            viewAllHref="/fagel"
            viewAllLabel="Se allt för fågel"
            products={data?.animals?.fagel}
          />
        </AnimatedSection>
      </LazySection>

      <LazySection minHeight="520px" rootMargin="400px">
        <AnimatedSection>
          <AnimalProductsBlock
            title="Utvalt för reptil"
            subtitle="Terrarier, värme och foder för reptiler"
            animalTag="reptil"
            viewAllHref="/reptil"
            viewAllLabel="Se allt för reptil"
            bgClass="bg-muted/30"
            products={data?.animals?.reptil}
          />
        </AnimatedSection>
      </LazySection>

      <LazySection minHeight="520px" rootMargin="400px">
        <AnimatedSection>
          <AnimalProductsBlock
            title="Utvalt för akvarium"
            subtitle="Akvarier, fiskmat och tillbehör för ditt akvarium"
            animalTag="akvarium"
            viewAllHref="/akvarium"
            viewAllLabel="Se allt för akvarium"
            products={data?.animals?.akvarium}
          />
        </AnimatedSection>
      </LazySection>

      {/* ── 14. DÄRFÖR HANDLAR KUNDER HOS ZOOI ── */}
      <LazySection minHeight="350px" rootMargin="400px">
        <WhyZooi />
      </LazySection>

      {/* ── 15. TIPS & RÅD ── */}
      <LazySection minHeight="450px" rootMargin="400px">
        <AnimatedSection>
          <TipsSection />
        </AnimatedSection>
      </LazySection>

      {/* ── 16. VARUMÄRKEN ── */}
      <LazySection minHeight="250px" rootMargin="400px">
        <AnimatedSection>
          <PopularBrands />
        </AnimatedSection>
      </LazySection>

      {/* ── 17. NYHETSBREV ── */}
      <LazySection minHeight="380px" rootMargin="400px">
        <AnimatedSection>
          <section className="py-12 md:py-16 bg-primary-50/40">
            <div className="container px-4 md:px-6">
              <div className="max-w-xl mx-auto text-center">
                <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-2">
                  Nyhetsbrev
                </p>
                <h2 className="text-xl md:text-2xl font-bold mb-2">Missa inga erbjudanden</h2>
                <p className="text-muted-foreground text-sm mb-6">
                  Få rabattkoder, tips och nyheter direkt i din inbox.
                </p>
                <div className="bg-background rounded-2xl p-6 shadow-sm border border-border">
                  <NewsletterSignupSimple />
                </div>
              </div>
            </div>
          </section>
        </AnimatedSection>
      </LazySection>

      {/* ── 18. SEO-TEXT ── */}
      <LazySection minHeight="300px" rootMargin="400px">
        <HomepageSEOContent />
      </LazySection>
    </>
  );
};

export default CampaignHomePage;
